import { z } from "zod";
import { PHOTO_LINES_MAX, Scene, VideoStyle } from "./scene.js";
import { Box, TOUR_STOPS_MAX, TourStopView } from "./tour.js";
import { OPENING_HOOK } from "./hook.js";

/** Engine-independent render input (05). URLs are `asset://<key>` in storage; the renderer resolves them to fetchable URLs at render time. */
const Rect = z.object({ x: z.number(), y: z.number(), w: z.number(), h: z.number() });
const Anchor = z.enum(["top", "center", "bottom", "lower_third"]);
const Url = z.url();

/**
 * A photo piece's photo as the renderer draws it (2026-09-29, ADR 0058 point 11): the owner's photo, its own size to
 * lay it out, and the box of the region each line pushes in on, in the scene's order (`Box`, as a tour's). A line past
 * the last box keeps the framing before it. Resolved at compose from the shot's image, as a tour is, and written only
 * when there is a photo to show: without it the piece plays the same moves on its own ground.
 */
export const ShotPhoto = z.object({ url: Url, width: z.number().int().positive(), height: z.number().int().positive(), boxes: z.array(Box).max(PHOTO_LINES_MAX) });
export type ShotPhoto = z.infer<typeof ShotPhoto>;

export const CompositionShot = z.object({
  id: z.string().min(1),
  startMs: z.number().int().min(0),
  durationMs: z.number().int().min(100),
  /** The picture. Absent exactly when `scene` is set: a drawn shot has no picture (ADR 0050). */
  media: z.object({ kind: z.enum(["image", "video"]), url: Url, fit: z.enum(["cover", "contain"]).default("cover") }).optional(),
  scene: Scene.optional(),
  motion: z.union([z.object({ kind: z.literal("none") }), z.object({ kind: z.literal("kenburns"), from: Rect, to: Rect }),
    // A photo toured by our camera (`tour.ts`): the photo's own size, to lay it out, and each stop's box in it.
    z.object({ kind: z.literal("tour"), image: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }), stops: z.array(TourStopView).min(1).max(TOUR_STOPS_MAX) })]).default({ kind: "none" }),
  transitionIn: z.object({ kind: z.enum(["cut", "fade", "slide"]), durationMs: z.number().int().min(0).max(2_000) }).default({ kind: "cut", durationMs: 0 }),
  text: z.array(z.object({ text: z.string().max(120), style: z.string(), position: Anchor })).default([]),
  /**
   * Narration for this shot, played from the shot's start, or `atMs` after it. The pipeline produces one clip per shot.
   * A showreel piece's voice is held back until the piece can land its first line (`voiceLead`, at most 500 ms, in
   * whole frames), and `said` is when it says each of the piece's lines and its figure, and when its last word ends, in
   * ms from the shot's first frame with the hold in them (`saidTimes`): the piece lands each line on its word, and the
   * worker's sounds and the renderer's picture both read them through `showreelSeams`. Measured from the voice, so
   * carried here and made again at every compose. Declared, not passed through: the worker reads the shots before they
   * are parsed, and an undeclared key would reach its sounds and not the picture.
   */
  voice: z.object({
    url: Url, gainDb: z.number().default(0),
    atMs: z.number().int().min(0).max(500).optional(),
    said: z.object({ lines: z.array(z.number().int().min(0).nullable()).max(3), figure: z.number().int().min(0).optional(), endMs: z.number().int().min(0) }).optional(),
  }).optional(),
  /**
   * A photo piece's photo, resolved (`ShotPhoto`). The shot is still a scene, not a picture. Declared for the same reason
   * as `voice`: the worker reads the shots before they are parsed and the renderer after, and the photo moves only where
   * the camera looks, never when anything lands, so nothing the worker times reads it (2026-09-29).
   */
  photo: ShotPhoto.optional(),
}).refine((s) => !!s.media !== !!s.scene, { message: "a shot is a picture or a scene, exactly one" })
  .refine((s) => !s.photo || (s.scene?.kind === "showreel" && s.scene.piece === "photo"), { message: "only a photo piece carries a photo" });

export const Composition = z.object({
  /** 2 since drawn scenes (ADR 0050); a version-1 spec is a version-2 spec with no scenes, and still renders. */
  specVersion: z.union([z.literal(1), z.literal(2)]),
  size: z.object({ width: z.number().int().positive(), height: z.number().int().positive() }),
  fps: z.literal(30),
  tracks: z.object({
    shots: z.array(CompositionShot).min(1),
    voice: z.object({ url: Url, startMs: z.number().int().min(0), gainDb: z.number() }).optional(),
    music: z.object({ url: Url, startMs: z.number().int().min(0), gainDb: z.number(), duckUnderVoice: z.boolean() }).optional(),
    /**
     * A sound on each thing a drawn scene lands (`hitsOf`): a whoosh as a heading arrives, a pop for each item of a list,
     * a thud for a stamp or a figure. Drawn by code like the music, and never dipped under the voice, which it is short
     * enough to sit between (docs/30).
     */
    hits: z.object({ url: Url, startMs: z.number().int().min(0), gainDb: z.number() }).optional(),
    captions: z
      .object({
        words: z.array(z.object({ text: z.string(), startMs: z.number().int(), endMs: z.number().int() })),
        style: z.string(),
      })
      .optional(),
    overlays: z
      .array(z.object({ kind: z.enum(["logo_bug", "lower_third", "end_card", "cta", "animation"]), spec: z.unknown(), startMs: z.number().int(), durationMs: z.number().int() }))
      .default([]),
  }),
  /** How the drawn scenes look as a whole (`VideoStyle`); absent is `clean`, and always absent on a pictures video (`drawnStyle`). */
  style: VideoStyle.optional(),
  /**
   * How the video opens: `hook`, its first shot's own words set huge and landed on frame 0, then settling into the shot
   * (`OPENING_HOOK`, 2026-10-13), so frame 0 is its cover (`coverAtMs`). `compose` writes it on every new video. Absent
   * on a composition made before, which renders, is covered and sounds as it did when it was made: its hits are in its
   * stored WAV, and its cover is read off its first shot.
   */
  opening: z.literal(OPENING_HOOK).optional(),
  /** The deterministic brand system; never asked of an image model. */
  brand: z.object({ palette: z.array(z.string().regex(/^#[0-9a-fA-F]{6}$/)).min(1).max(6), fontFamily: z.string(), logoUrl: Url.optional() }),
});
export type Composition = z.infer<typeof Composition>;

/**
 * How long the brand's card at the end of a video takes to come in over its last shot (docs/05): a crossfade, as a
 * picture comes in over the one before it, so the picture never dips to black on the way.
 */
export const END_CARD_FADE_MS = 300;

/**
 * The frames the brand's card takes to come in (`END_CARD_FADE_MS`): its ground over all of them, a last card scene's
 * words leaving over the first half and the card's own logo and words arriving over the second. One rounding for the
 * renderer that draws it and the cover that keeps clear of it (`coverAtMs`), so the two cannot disagree by a frame.
 */
export const endCardFadeFrames = (fps: number) => Math.round((END_CARD_FADE_MS / 1000) * fps);

/**
 * How long a video lasts: to the end of its last shot, or of an overlay that runs past it. The brand's card is its own
 * stretch after the last shot (2026-10-12), and the renderer and the render's meter both read its length here.
 */
export function compositionDurationMs(c: Composition): number {
  return Math.max(...c.tracks.shots.map((s) => s.startMs + s.durationMs), ...c.tracks.overlays.map((o) => o.startMs + o.durationMs));
}
